// Simple entry point file
console.log(`Main entry point loaded 730`);

// Import SCSS
import './scss/main.scss';

// Import bootstrap themes if needed
import './app/lib/bs-theme.js';

// Import optimized image handling
import { enhanceImages } from './app/utils/optimizedImage.js';

// Import lazy loading function using default import
import startLazyLoading from './app/utils/startLaizyLoading.js';

// Global coordination variables for URL fixing
window.__urlFixesApplied = window.__urlFixesApplied || false;
window.__urlFixesRunning = window.__urlFixesRunning || false;

// More targeted URL fixing - run only if not already handled by fixurls.js
const fixAllPageContent = () => {
  // Skip if already fixed or being handled by fixurls.js
  if (window.__urlFixesApplied || window.__urlFixesRunning) {
    console.log('URL fixes already applied or being handled by fixurls.js, skipping duplicate fixes');
    return;
  }

  // Mark that main.js is handling URL fixes
  window.__urlFixesRunning = true;
  
  // Fix HTML content - targeted approach
  if (document.documentElement.innerHTML.includes('localhost')) {
    console.log('Found localhost references in HTML, fixing from main.js...');
    
    // Fix image sources
    document.querySelectorAll('img[src*="localhost"]').forEach(img => {
      try {
        const url = new URL(img.src);
        img.src = url.pathname;
      } catch (e) {
        console.error('Error fixing image src:', e);
      }
    });
    
    // Fix data-src attributes
    document.querySelectorAll('[data-src*="localhost"]').forEach(el => {
      try {
        const url = new URL(el.dataset.src);
        el.dataset.src = url.pathname; 
      } catch (e) {
        console.error('Error fixing data-src:', e);
      }
    });
    
    // Record that fixes were applied
    window.__urlFixesApplied = true;
    console.log('URL fixes completed by main.js');
  } else {
    window.__urlFixesApplied = true;
  }
  
  // Signal that main.js is done with URL fixing
  window.__urlFixesRunning = false;
};

// Run this before any other code
try {
  fixAllPageContent();
} catch (e) {
  console.error('Error fixing page content:', e);
  window.__urlFixesRunning = false;
}

// Block insecure resource loads before they happen
const setupResourceBlocker = () => {
  // Create a MutationObserver to watch for new elements being added to the DOM
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Check for added nodes
      mutation.addedNodes.forEach((node) => {
        // Only process element nodes
        if (node.nodeType === 1) {
          // Skip if this is our secure font link
          if (node.dataset && node.dataset.secureFont === 'true') {
            return;
          }

          // Block insecure scripts
          if (node.tagName === 'SCRIPT' && node.src) {
            // Force HTTPS for all external scripts
            if (node.src.startsWith('http://')) {
              console.warn('Blocked insecure script:', node.src);
              node.src = node.src.replace('http://', 'https://');
            }
            
            // Fix hostname if needed for viewingparty.net content
            if (node.src.includes('viewingparty.net/') && !node.src.startsWith('https://')) {
              console.warn('Fixed script URL:', node.src);
              node.src = 'https://viewingparty.net/' + node.src.split('viewingparty.net/')[1];
            }
          }
          
          // Block insecure stylesheets
          if (node.tagName === 'LINK' && node.rel === 'stylesheet' && 
              node.href && node.href.startsWith('http://')) {
            console.warn('Blocked insecure stylesheet:', node.href);
            node.href = node.href.replace('http://', 'https://');
          }
        }
      });
    });
  });

  // Start observing the document body for changes
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
};

// Add Lato font with HTTPS to prevent mixed content
const addLatoFont = () => {
  // Only add if not already present
  if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Lato"]')) {
    const latoLink = document.createElement('link');
    latoLink.rel = 'stylesheet';
    latoLink.href = 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap';
    // Add a data attribute to mark this as our secure font link
    latoLink.dataset.secureFont = 'true';
    document.head.appendChild(latoLink);
  }
};

// Helper to fix all URLs in document
document.addEventListener('DOMContentLoaded', () => {
  // Set up blocking of insecure resources
  setupResourceBlocker();
  
  // Add Lato font explicitly with HTTPS
  addLatoFont();

  // Check if we still need to do final URL fixes
  const needsFinalFixes = !window.__urlFixesApplied && 
                          document.documentElement.innerHTML.includes('localhost');
  
  if (needsFinalFixes && !window.__urlFixesRunning) {
    console.log('Running final URL fixes from main.js...');
    
    // Mark as running to prevent conflicts
    window.__urlFixesRunning = true;
    
    // Fix Google Fonts and other external resources using HTTP
    document.querySelectorAll('link[href^="http://"]').forEach(link => {
      link.href = link.href.replace('http://', 'https://');
    });

    // Fix stylesheets using HTTP
    document.querySelectorAll('link[rel="stylesheet"][href^="http://"]').forEach(link => {
      link.href = link.href.replace('http://', 'https://');
    });

    // Fix all localhost images
    document.querySelectorAll('img[src*="localhost"]').forEach(img => {
      const url = new URL(img.src);
      img.src = url.pathname;
    });

    // Fix all background images with localhost in styles
    document.querySelectorAll('[style*="localhost"]').forEach(el => {
      let style = el.getAttribute('style');
      if (style) {
        // Extract URL, remove localhost part, keep path
        style = style.replace(/url\(['"]?http:\/\/localhost:[0-9]+([^'"]+)['"]?\)/g, "url('$1')");
        // Ensure paths start with / if they don't already
        style = style.replace(/url\(['"]?([^\/'][^'"]+)['"]?\)/g, "url('/$1')");
        el.setAttribute('style', style);
      }
    });

    // Fix data-src attributes for lazy loading
    document.querySelectorAll('[data-src*="localhost"]').forEach(el => {
      const url = new URL(el.dataset.src);
      el.dataset.src = url.pathname;
    });

    // Fix any relative data-src that doesn't start with /
    document.querySelectorAll('[data-src]').forEach(el => {
      if (el.dataset.src && !el.dataset.src.startsWith('/') && !el.dataset.src.startsWith('http')) {
        el.dataset.src = '/' + el.dataset.src;
      }
    });
    
    // Mark as complete
    window.__urlFixesApplied = true;
    window.__urlFixesRunning = false;
    
    console.log('Final URL fixes completed by main.js');
  }
  
  // Initialize lazy loading with a short delay
  setTimeout(() => {
    console.log("Direct lazy loading initialization from main.js");
    try {
      // Try to import the lazy loading function directly
      import(/* webpackChunkName: "lazyload" */ './app/utils/startLaizyLoading.js')
        .then(module => {
          try {
            module.default({
              debug: process.env.NODE_ENV !== 'production', // Enable debug only in development
              // Other options...
            });
            console.log('Successfully initialized lazy loading');
          } catch (err) {
            console.error('Error initializing lazy loading:', err);
          }
        })
        .catch(err => {
          console.error('Error importing lazy loading module:', err);
          
          // Fallback to directly loading the script if dynamic import fails
          loadScriptSecurely('/js/lazyload.js')
            .then(() => console.log('Successfully loaded lazyload.js via fallback'))
            .catch(err => console.error('Failed to load lazyload.js via fallback:', err));
        });
    } catch (e) {
      console.error("Error initializing lazy loading:", e);
    }
  }, 800);

  // Enhance all images with proper picture tags and formats
  enhanceImages();

  // Start lazy loading for relevant elements
  startLazyLoading();
  console.log('Lazy loading initiated');
});

// Helper to safely load a script with HTTPS enforcement
const loadScriptSecurely = (url, async = true, defer = false) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    
    // Ensure we're using HTTPS
    let secureUrl = url;
    if (secureUrl.startsWith('http://')) {
      secureUrl = secureUrl.replace('http://', 'https://');
    } 
    
    // If no protocol specified but has domain, add https://
    if (!secureUrl.startsWith('http') && !secureUrl.startsWith('/') && 
        secureUrl.includes('.')) {
      secureUrl = 'https://' + secureUrl;
    }
    
    // For viewingparty.net URLs, ensure proper formatting
    if (secureUrl.includes('viewingparty.net/') && !secureUrl.startsWith('https://')) {
      secureUrl = 'https://viewingparty.net/' + secureUrl.split('viewingparty.net/')[1];
    }
    
    // For relative URLs, keep as is
    if (!secureUrl.startsWith('http') && !secureUrl.includes('viewingparty.net')) {
      // Ensure it starts with / for absolute path from root
      if (!secureUrl.startsWith('/')) {
        secureUrl = '/' + secureUrl;
      }
    }
    
    script.src = secureUrl;
    script.async = async;
    script.defer = defer;
    
    script.onload = () => {
      console.log(`Successfully loaded script: ${secureUrl}`);
      resolve(script);
    };
    
    script.onerror = (error) => {
      console.error(`Failed to load script: ${secureUrl}`, error);
      reject(error);
    };
    
    document.head.appendChild(script);
  });
}; 